import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useAppDispatch } from "@/redux/features/hooks";
import toast from "react-hot-toast";
import {
  OrderApi,
  useChangeOrderStatusMutation,
  useGetOrdersQuery,
  useUpdateComboItemStatusMutation,
  useUpdateFormulaItemStatusMutation,
  useUpdateOrderItemStatusMutation,
} from "@/redux/services/order/order.api";
import { Order, OrderStatus } from "@/redux/services/order/order.type";
import { useMercureSubscription } from "@/lib/hooks";
import {
  playNotificationSound,
  primeNotificationAudio,
} from "@/lib/audioUtils";
import { createKdsChime, KDS_STATUSES } from "@/lib/kdsChime";
import { normalizeOrder } from "@/redux/services/order/normalizeOrder";
import { cn } from "@/lib/utils";
import {
  KDS_COLUMN_ORDER,
  KdsColumn,
  KdsMoveStep,
  kdsColumnFor,
  planKdsMove,
} from "@/lib/kdsMove";
import KdsOrderCard from "./KdsOrderCard";
import DraggableOrderCard from "./DraggableOrderCard";
import DroppableColumn from "./DroppableColumn";
import {
  ChefHat,
  Eye,
  EyeOff,
  PanelTopClose,
  PanelTopOpen,
} from "lucide-react";
import { endOfDay, startOfDay } from "date-fns";
import {
  CollisionDetection,
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";

interface KdsBoardProps {
  restaurantId: string;
  hideTopBar: boolean;
  onToggleTopBar: () => void;
}

const COLUMN_CONFIG: {
  key: KdsColumn;
  label: string;
  statuses: OrderStatus[];
  targetStatus: OrderStatus;
  accentClass: string;
}[] = [
  {
    key: "waiting",
    label: "Nouvelles",
    statuses: ["accepted"],
    targetStatus: "accepted",
    accentClass: "bg-primary",
  },
  {
    key: "preparing",
    label: "En cours",
    statuses: ["preparing"],
    targetStatus: "preparing",
    accentClass: "bg-warning",
  },
  {
    key: "ready",
    label: "Prêt",
    statuses: ["ready"],
    targetStatus: "ready",
    accentClass: "bg-success",
  },
];

const READY_HIDDEN_KEY = "kitchen.readyColumnHidden";

// Only register a column as a drop target once the card overlaps it by at
// least 80% of the card's own area — prevents an accidental status change
// when only a tip of the card crosses the column edge.
const DROP_OVERLAP_THRESHOLD = 0.8;

const majorityOverlap: CollisionDetection = ({
  collisionRect,
  droppableContainers,
}) => {
  const activeArea = collisionRect.width * collisionRect.height;
  if (activeArea === 0) return [];

  const collisions: {
    id: string | number;
    data: { droppableContainer: (typeof droppableContainers)[number]; value: number };
  }[] = [];

  for (const container of droppableContainers) {
    const rect = container.rect.current;
    if (!rect) continue;
    const overlapX = Math.max(
      0,
      Math.min(collisionRect.right, rect.right) -
        Math.max(collisionRect.left, rect.left),
    );
    const overlapY = Math.max(
      0,
      Math.min(collisionRect.bottom, rect.bottom) -
        Math.max(collisionRect.top, rect.top),
    );
    const ratio = (overlapX * overlapY) / activeArea;
    if (ratio >= DROP_OVERLAP_THRESHOLD) {
      collisions.push({
        id: container.id,
        data: { droppableContainer: container, value: ratio },
      });
    }
  }

  return collisions.sort((a, b) => b.data.value - a.data.value);
};

const KdsBoard: React.FC<KdsBoardProps> = ({
  restaurantId,
  hideTopBar,
  onToggleTopBar,
}) => {
  const dispatch = useAppDispatch();
  const [activeTab, setActiveTab] = useState<KdsColumn>("waiting");
  const [hideReady, setHideReady] = useState<boolean>(
    () => localStorage.getItem(READY_HIDDEN_KEY) === "true",
  );
  const [, setNow] = useState(Date.now());

  useEffect(() => {
    localStorage.setItem(READY_HIDDEN_KEY, String(hideReady));
  }, [hideReady]);

  // If ready was the active mobile tab when the user hides it, fall back
  // to a still-visible tab.
  useEffect(() => {
    if (hideReady && activeTab === "ready") setActiveTab("preparing");
  }, [hideReady, activeTab]);
  // Decides which order events ring (new kitchen work, fresh add-ons).
  const chimeRef = useRef(createKdsChime());
  const initialLoadDone = useRef(false);

  // Browsers may block audio until the user interacts with the page; unlock
  // the notification sound on the first tap/key press on the board.
  useEffect(() => {
    const unlock = () => {
      primeNotificationAudio();
      window.removeEventListener("pointerdown", unlock);
      window.removeEventListener("keydown", unlock);
    };
    window.addEventListener("pointerdown", unlock);
    window.addEventListener("keydown", unlock);
    return () => {
      window.removeEventListener("pointerdown", unlock);
      window.removeEventListener("keydown", unlock);
    };
  }, []);

  // Tick every 30s so age-based UI stays current without refetch
  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(interval);
  }, []);

  const todayRange = useMemo(() => {
    const now = new Date();
    return {
      after: startOfDay(now).toISOString(),
      before: endOfDay(now).toISOString(),
    };
  }, []);

  const queryArgs = useMemo(
    () => ({
      restaurantId,
      query: {
        status: "accepted,preparing,ready" as any,
        limit: 100,
        "order[createdAt]": "asc" as const,
        "createdAt[after]": todayRange.after,
        "createdAt[before]": todayRange.before,
      },
    }),
    [restaurantId, todayRange],
  );

  // Add-ons can land on an already ready/served order. Those orders aren't in
  // the main board query, so fetch them separately — but only while they carry
  // unprepared work, so a finished order drops off the board automatically.
  const addOnQueryArgs = useMemo(
    () => ({
      restaurantId,
      query: {
        status: "served" as any,
        hasUnpreparedItems: true,
        limit: 100,
        "order[createdAt]": "asc" as const,
        "createdAt[after]": todayRange.after,
        "createdAt[before]": todayRange.before,
      },
    }),
    [restaurantId, todayRange],
  );

  const { data: orderData, isLoading } = useGetOrdersQuery(queryArgs);
  const { data: addOnData, refetch: refetchAddOns } =
    useGetOrdersQuery(addOnQueryArgs);

  const orders = useMemo(() => {
    const main = orderData?.results ?? [];
    const addOns = addOnData?.results ?? [];
    if (addOns.length === 0) return main;
    const seen = new Set(main.map((o) => o.id));
    return [...main, ...addOns.filter((o) => !seen.has(o.id))];
  }, [orderData, addOnData]);

  const orderEvent = useMercureSubscription<Order>(
    restaurantId ? `/api/restaurants/${restaurantId}/orders` : null,
    ["order:created", "order:updated", "order:item_status_updated"],
  );

  // Served orders already showing as add-on work, so we don't re-ring on them.
  useEffect(() => {
    if (addOnData?.results) {
      chimeRef.current.seedAddOns(addOnData.results.map((o) => o.id));
    }
  }, [addOnData]);

  // Orders already on the board at first load don't ring.
  useEffect(() => {
    if (orderData?.results && !initialLoadDone.current) {
      chimeRef.current.seed(orderData.results.map((o) => o.id));
      initialLoadDone.current = true;
    }
  }, [orderData]);

  // Mercure events are now used to patch the cache directly — fast for
  // multi-client updates (e.g. another kitchen screen, manager actions).
  // Originating-client actions are already handled via optimistic updates in
  // the mutation's onQueryStarted, so this path is mostly a no-op for them.
  useEffect(() => {
    if (!orderEvent) return;

    const newOrder = normalizeOrder(orderEvent.data);
    if (!newOrder?.id) return;

    const isKdsOrder = KDS_STATUSES.includes(newOrder.status);

    // Rings the first time an order enters a kitchen status, on any event
    // (a POS order is created pending and accepted by a later update), and
    // when an add-on lands on a served order.
    if (chimeRef.current.shouldRing(newOrder)) {
      playNotificationSound();
    }

    dispatch(
      OrderApi.util.updateQueryData("getOrders", queryArgs, (draft) => {
        const idx = draft.results.findIndex((o) => o.id === newOrder.id);

        if (isKdsOrder) {
          if (idx !== -1) {
            draft.results[idx] = newOrder;
          } else {
            draft.results.push(newOrder);
            draft.results.sort(
              (a, b) =>
                new Date(a.createdAt).getTime() -
                new Date(b.createdAt).getTime(),
            );
            draft.count = (draft.count ?? 0) + 1;
          }
        } else if (idx !== -1) {
          draft.results.splice(idx, 1);
          draft.count = Math.max(0, (draft.count ?? 0) - 1);
        }
      }),
    );

    // Served orders surface on the board only while they carry an unprepared
    // add-on. Refetch that collection so cards appear/drop live.
    if (newOrder.status === "served") {
      refetchAddOns();
    }
  }, [orderEvent, dispatch, queryArgs, refetchAddOns]);

  const groupedOrders = useMemo(() => {
    const groups: Record<KdsColumn, Order[]> = {
      waiting: [],
      preparing: [],
      ready: [],
    };
    for (const order of orders) {
      // A ready/served order with a fresh add-on stays in "En cours" until
      // every line is ready (see kdsColumnFor).
      const col = kdsColumnFor(order);
      if (col) groups[col].push(order);
    }
    return groups;
  }, [orders]);

  const handleItemStatusChange = useCallback(() => {
    // Cache invalidation via mutation will refetch
  }, []);

  const [changeOrderStatus] = useChangeOrderStatusMutation();
  const [updateOrderItemStatus] = useUpdateOrderItemStatusMutation();
  const [updateComboItemStatus] = useUpdateComboItemStatusMutation();
  const [updateFormulaItemStatus] = useUpdateFormulaItemStatusMutation();
  const [activeOrderId, setActiveOrderId] = useState<string | null>(null);

  // 300ms press-and-hold on both pointers prevents accidental drags from a
  // stray click and lets staff scroll/tap freely; only a deliberate hold
  // initiates a drag.
  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: { delay: 300, tolerance: 8 },
    }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 300, tolerance: 8 },
    }),
  );

  const activeOrder = useMemo(
    () => (activeOrderId ? orders.find((o) => o.id === activeOrderId) : null),
    [activeOrderId, orders],
  );

  const activeColumnIdx = useMemo(() => {
    if (!activeOrder) return -1;
    const col = kdsColumnFor(activeOrder);
    return col ? KDS_COLUMN_ORDER.indexOf(col) : -1;
  }, [activeOrder]);

  const handleDragStart = useCallback((event: DragStartEvent) => {
    setActiveOrderId(event.active.id as string);
  }, []);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      setActiveOrderId(null);
      const { active, over } = event;
      if (!over) return;

      const order = orders.find((o) => o.id === active.id);
      if (!order) return;

      // Forward-only. Moving to "Prêt" advances the lines, like the line
      // buttons; the API then moves the order (see planKdsMove).
      const steps = planKdsMove(order, over.id as KdsColumn);
      if (steps.length === 0) return;

      const run = (step: KdsMoveStep) => {
        if (step.kind === "order") {
          return changeOrderStatus({ id: order.id, status: step.status });
        }
        const mutation =
          step.itemType === "combo"
            ? updateComboItemStatus
            : step.itemType === "formula"
              ? updateFormulaItemStatus
              : updateOrderItemStatus;
        return mutation({
          orderUuid: order.id,
          itemUuid: step.itemId,
          status: step.status,
        });
      };

      void (async () => {
        try {
          // Sequential: each step depends on the previous one (workflow).
          for (const step of steps) {
            await run(step).unwrap();
          }
        } catch {
          // Stop at the first refused step; what already went through stays.
          toast.error("Impossible de déplacer la commande");
        }
      })();
    },
    [
      orders,
      changeOrderStatus,
      updateOrderItemStatus,
      updateComboItemStatus,
      updateFormulaItemStatus,
    ],
  );

  const handleDragCancel = useCallback(() => {
    setActiveOrderId(null);
  }, []);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <ChefHat className="h-10 w-10 text-primary animate-pulse" />
      </div>
    );
  }

  const visibleColumns = hideReady
    ? COLUMN_CONFIG.filter((c) => c.key !== "ready")
    : COLUMN_CONFIG;
  const readyCount = groupedOrders.ready.length;

  return (
    <div className="space-y-3">
      {/* Top toolbar — topbar visibility + Prêt column visibility */}
      <div className="flex items-center justify-end gap-2">
        <button
          type="button"
          onClick={onToggleTopBar}
          aria-pressed={hideTopBar}
          aria-label={
            hideTopBar
              ? "Afficher la barre supérieure"
              : "Masquer la barre supérieure"
          }
          className={cn(
            "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors",
            "bg-white text-muted-foreground border-border hover:text-card-foreground",
          )}
        >
          {hideTopBar ? (
            <PanelTopOpen className="h-3.5 w-3.5" />
          ) : (
            <PanelTopClose className="h-3.5 w-3.5" />
          )}
          {hideTopBar ? "Afficher la barre" : "Masquer la barre"}
        </button>
        <button
          type="button"
          onClick={() => setHideReady((v) => !v)}
          aria-pressed={hideReady}
          aria-label={
            hideReady
              ? "Afficher la colonne Prêt"
              : "Masquer la colonne Prêt"
          }
          className={cn(
            "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors",
            "bg-white text-muted-foreground border-border hover:text-card-foreground",
          )}
        >
          {hideReady ? (
            <Eye className="h-3.5 w-3.5" />
          ) : (
            <EyeOff className="h-3.5 w-3.5" />
          )}
          {hideReady
            ? `Afficher « Prêt »${readyCount > 0 ? ` (${readyCount})` : ""}`
            : "Masquer « Prêt »"}
        </button>
      </div>

      {/* Mobile: tabs */}
      <div className="md:hidden">
        <div className="flex border-b border-border">
          {visibleColumns.map((col) => {
            const count = groupedOrders[col.key].length;
            return (
              <button
                key={col.key}
                type="button"
                onClick={() => setActiveTab(col.key)}
                className={cn(
                  "flex-1 flex items-center justify-center gap-2 py-3 px-1 min-h-[56px] text-base font-bold transition-colors",
                  activeTab === col.key
                    ? "text-primary border-b-2 border-primary"
                    : "text-muted-foreground border-b-2 border-transparent",
                )}
              >
                <span>{col.label}</span>
                <span
                  className={cn(
                    "inline-flex items-center justify-center min-w-[24px] h-6 px-1.5 rounded-full text-xs font-bold",
                    activeTab === col.key
                      ? "bg-primary/10 text-primary"
                      : "bg-muted text-muted-foreground",
                  )}
                >
                  {count}
                </span>
              </button>
            );
          })}
        </div>

        <div className="space-y-3 pt-3 pb-4">
          {groupedOrders[activeTab].length === 0 ? (
            <EmptyColumn />
          ) : (
            groupedOrders[activeTab].map((order) => (
              <KdsOrderCard
                key={order.id}
                order={order}
                onItemStatusChange={handleItemStatusChange}
              />
            ))
          )}
        </div>
      </div>

      {/* Desktop: 3-column board with drag-and-drop */}
      <DndContext
        sensors={sensors}
        collisionDetection={majorityOverlap}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}
      >
        <div
          className={cn(
            "hidden md:grid md:gap-4",
            hideReady ? "md:grid-cols-2" : "md:grid-cols-3",
          )}
        >
          {visibleColumns.map((col, colIdx) => {
            const count = groupedOrders[col.key].length;
            const isValidTarget =
              activeOrderId !== null && colIdx > activeColumnIdx;
            return (
              <DroppableColumn
                key={col.key}
                id={col.key}
                isValidTarget={isValidTarget}
              >
                <header className="relative bg-card border-b border-border px-4 py-3">
                  <span
                    className={cn(
                      "absolute left-0 top-0 h-full w-1.5",
                      col.accentClass,
                    )}
                    aria-hidden
                  />
                  <div className="flex items-center justify-between gap-2 pl-3">
                    <h2 className="text-base font-bold text-card-foreground uppercase tracking-wide">
                      {col.label}
                    </h2>
                    <span className="text-2xl font-extrabold tabular-nums text-card-foreground">
                      {count}
                    </span>
                  </div>
                </header>

                <div className="flex-1 p-3 space-y-3 overflow-y-auto max-h-[calc(100vh-220px)]">
                  {count === 0 ? (
                    <EmptyColumn />
                  ) : (
                    groupedOrders[col.key].map((order) => (
                      <DraggableOrderCard
                        key={order.id}
                        order={order}
                        onItemStatusChange={handleItemStatusChange}
                      />
                    ))
                  )}
                </div>
              </DroppableColumn>
            );
          })}
        </div>

        <DragOverlay dropAnimation={null}>
          {activeOrder ? (
            <div className="cursor-grabbing rotate-2 scale-105 shadow-2xl ring-2 ring-primary/40 rounded-lg">
              <KdsOrderCard order={activeOrder} />
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>
    </div>
  );
};

const EmptyColumn = () => (
  <div className="flex flex-col items-center justify-center text-center py-12 px-4">
    <ChefHat className="h-8 w-8 text-muted-foreground/40" />
    <p className="text-sm text-muted-foreground mt-2">Aucune commande</p>
  </div>
);

export default KdsBoard;
